import { TranslocoDirective, TranslocoPipe, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatChipSelectionChange, MatChipsModule } from '@angular/material/chips';
import { PriorityStatus, TableView, SortColumn, TypeOfColumn, TableColumnInfo } from 'design-system'
import { TASKS_DESCRIBE } from '../constants';
import { DynamicRouteService, HeaderService, Q8Breakpoints } from 'core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { finalize, forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TaskFilterDialogComponent } from '../../components/task-filter-dialog-component/task-filter-dialog-component';
import { TaskFilter } from '../../interfaces/task-filter';
import { FormBuilder, FormGroup, ReactiveFormsModule, ɵInternalFormsSharedModule } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { TaskApprovativo } from '../../interfaces/task-approvativo.model';
export enum TypeOfTaskStatus { TO_COMPLETE, PENDING, COMPLETED, REJECTED }
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { TaskFilterService } from '../../components/task-filter-dialog-component/task-filter.service';
import { TaskDateCell } from '../../components/task-date-cell/task-date-cell';
import { taskAnomalies } from '../../utils/task.utils';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  providers: [provideTranslocoScope('tasks')],
  selector: 'tasks-tasks-list',
  imports: [TranslocoPipe,
    MatChipsModule, CommonModule, DatePipe,
    PriorityStatus, MatButtonModule, MatButtonToggleModule, ReactiveFormsModule,
    MatFormFieldModule, MatInputModule, TableView, ɵInternalFormsSharedModule,
    MatCardModule, MatPaginatorModule, TranslocoDirective, TranslocoCurrencyPipe,
    MatDatepickerModule, MatDividerModule,
  ],
  templateUrl: './tasks-list.html',
  styleUrl: './tasks-list.scss',
})
export class TasksList implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreTrigger')
  private loadMoreTrigger!: ElementRef<HTMLElement>;

  TypeOfTaskStatus = TypeOfTaskStatus;

  readonly viewOptions = [
    { value: 'table', get label() { return translate<string>('tasks.ui.tabella'); }, iconClass: 'fa-regular fa-list' },
    { value: 'card', get label() { return translate<string>('tasks.ui.card'); }, iconClass: 'fa-regular fa-map' },
  ];
  readonly priorities = ['Low', 'Medium', 'High'];

  selectedView = 'table';
  isMobile = false;
  filtersOpen = false;
  tipiTask: string[] = [];

  newTaskCount: number = 0;
  pendingTaskCount: number = 0;
  completedTaskCount: number = 0;
  rejectedTaskCount: number = 0;

  tasksArray: TaskApprovativo[] = [];
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  currentSortColumns: SortColumn[] = [];
  tasksLoading = false;

  // TODO: in futuro si potrebbe usare solo filtersForm, aggiungendo statusType/stato come FormControl
  // e leggendo tutto da getRawValue() in loadTasks(), come fa loadCustomers() in customers-list.
  filters: TaskFilter = { statusType: TypeOfTaskStatus.TO_COMPLETE };
  filtersForm!: FormGroup;
  readonly currencyOptions = { useGrouping: true };

  titleLabel: string = "";
  columnsInfo: TableColumnInfo[] = [];

  readonly statusChips = [
    { status: TypeOfTaskStatus.TO_COMPLETE, iconClass: 'fa-regular fa-list-check', labelKey: 'filter.toComplete', countProp: 'newTaskCount' },
    { status: TypeOfTaskStatus.PENDING, iconClass: 'fa-regular fa-spinner', labelKey: 'filter.pending', countProp: 'pendingTaskCount' },
    { status: TypeOfTaskStatus.COMPLETED, iconClass: 'fa-regular fa-circle-check', labelKey: 'filter.completed', countProp: 'completedTaskCount' },
    { status: TypeOfTaskStatus.REJECTED, iconClass: 'fa-regular fa-circle-xmark', labelKey: 'filter.rejected', countProp: 'rejectedTaskCount' }
  ];

  private headerService = inject(HeaderService);
  private breakpointObserver = inject(BreakpointObserver);
  private tasksInitialized = false;
  private tasksLoadRequestId = 0;
  private tasksObserver?: IntersectionObserver;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private dynamicRouteService: DynamicRouteService,
    private http: HttpClient,
    private translocoService: TranslocoService,
    private taskFilterService: TaskFilterService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      testo: [''],
      nome: [[] as string[]],
      priorita: [[] as string[]],
      dataInizio: [null as string | null],
      dataFine: [null as string | null],
    });

    const saved = this.taskFilterService.getSavedFilters();
    if (saved) {
      this.filters = { ...saved };
      this.filtersForm.patchValue({
        testo: saved.testo || '',
        nome: saved.nome || [],
        priorita: saved.priorita || [],
        dataInizio: saved.dataInizio || null,
        dataFine: saved.dataFine || null,
      }, { emitEvent: false });
    }

    this.loadAllCountsByStatus();
    this.taskFilterService.getTipiTask().subscribe(res => {
      this.tipiTask = res;
    });

    this.translocoService.selectTranslation('tasks').subscribe(() => {
      this.translateUI();
      if (this.filters.statusType !== undefined) {
        this.updateTitleLabel(this.filters.statusType);
      }
    });

    this.filtersForm.get('testo')!.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.filters.testo = value;
      this.applyAndSave();
    });

    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet - 1}px)`])
      .subscribe(result => {
        const modeChanged = this.tasksInitialized && this.isMobile !== result.matches;
        this.isMobile = result.matches;
        if (this.isMobile && this.filtersOpen) {
          this.filtersOpen = false;
        }

        if (this.isMobile) {
          this.headerService.setActions([{
            get label() { return translate<string>('tasks.ui.mostraFiltri'); },
            icon: 'fa-filter',
            action: () => this.openDialog(),
          }]);
        } else {
          this.headerService.removeActions();
        }

        if (!this.tasksInitialized || modeChanged) {
          this.pageIndex = 0;
          this.loadTasks(0, false);
        }
      });
  }

  ngAfterViewInit(): void {
    this.tasksObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadNextTasksPage();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0,
      },
    );

    this.tasksObserver.observe(this.loadMoreTrigger.nativeElement);
  }

  ngOnDestroy(): void {
    this.tasksObserver?.disconnect();
    this.tasksObserver = undefined;

    const destinationUrl = this.router.url;
    const moduleBaseRoute = this.activatedRoute.snapshot.parent?.url[0]?.path;
    // Se non trovo una base route(fallback) o se la pagina di destinazione è al di fuori del modulo: resetto i filtri
    if (!moduleBaseRoute || !destinationUrl.includes(moduleBaseRoute)) {
      // Reimposto to complete come chip di default
      this.taskFilterService.setSavedFilters({ statusType: TypeOfTaskStatus.TO_COMPLETE });
    }

    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  private translateUI() {
    this.titleLabel = this.translocoService.translate('tasks.taskList.filter.toComplete').toLowerCase();
    this.headerService.setTitle(this.translocoService.translate('tasks.taskList.header'));

    // Traduzione delle colonne
    this.columnsInfo = [
      {
        columnNameDef: "titolo",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.title'),
        type: TypeOfColumn.TEXT,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "nome",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.type'),
        type: TypeOfColumn.TEXT,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "descrizione",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.description'),
        type: TypeOfColumn.TEXT,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "notaUtente",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.userNote'),
        type: TypeOfColumn.TEXT,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "importo",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.amount'),
        type: TypeOfColumn.CURRENCY,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "dataTask",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.dateTime'),
        type: TypeOfColumn.COMPONENT,
        component: TaskDateCell,
        sortable: true,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "prossimoApprovatore",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.sentTo'),
        type: TypeOfColumn.TEXT,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "priorita",
        label: this.translocoService.translate('tasks.taskList.columnsInfo.priority'),
        type: TypeOfColumn.COMPONENT,
        component: PriorityStatus,
        sortable: true,
        labelOnOneLine: true,
      }
    ];
  }

  openDialog() {
    const dialogRef = this.dialog.open(TaskFilterDialogComponent, {
      data: { ...this.filters }
    });

    dialogRef.afterClosed().subscribe(filtersResult => {
      if (filtersResult) {
        this.filters = { ...this.filters, ...filtersResult };
        this.filtersForm.patchValue({
          nome: filtersResult.nome || [],
          priorita: filtersResult.priorita || [],
          dataInizio: filtersResult.dataInizio || null,
          dataFine: filtersResult.dataFine || null,
        }, { emitEvent: false });
        this.applyAndSave();
      }
    });
  }

  onFilterButtonClick(): void {
    if (this.isMobile) {
      this.openDialog();
      return;
    }
    this.filtersOpen = !this.filtersOpen;
  }

  resetFilters(): void {
    this.filtersForm.patchValue({
      nome: [],
      priorita: [],
      dataInizio: null,
      dataFine: null,
    });
    this.filters.nome = undefined;
    this.filters.priorita = undefined;
    this.filters.dataInizio = undefined;
    this.filters.dataFine = undefined;
    this.pageIndex = 0;
    this.applyAndSave();
  }

  applyFilters(): void {
    const v = this.filtersForm.getRawValue();
    this.filters.nome = v.nome?.length ? v.nome : undefined;
    this.filters.priorita = v.priorita?.length ? v.priorita : undefined;
    this.filters.dataInizio = v.dataInizio ?? undefined;
    this.filters.dataFine = v.dataFine ?? undefined;
    this.pageIndex = 0;
    this.applyAndSave();
  }

  onInlineTipoTaskSelect(event: any, id: string): void {
    let arr: string[] = [...(this.filtersForm.get('nome')?.value || [])];
    if (event.selected) {
      if (!arr.includes(id)) arr.push(id);
    } else {
      arr = arr.filter(i => i !== id);
    }
    this.filtersForm.get('nome')!.setValue(arr);
  }

  checkInlineTipoTaskSelected(id: string): boolean {
    return (this.filtersForm.get('nome')?.value || []).includes(id);
  }

  toggleInlinePriority(priority: string): void {
    const selectedPriorities: string[] = this.filtersForm.get('priorita')?.value || [];
    const priorita = selectedPriorities.includes(priority)
      ? selectedPriorities.filter(item => item !== priority)
      : [...selectedPriorities, priority];

    this.filtersForm.get('priorita')!.setValue(priorita);
  }

  checkInlinePrioritySelected(priority: string): boolean {
    return (this.filtersForm.get('priorita')?.value || []).includes(priority);
  }

  loadTasks(pageIndex = this.pageIndex, append = false) {
    const searchObject: {
      NomeTipiTask: string[];
      Priorita: string[];
      Stato: number[];
      DataInizio?: string;
      DataFine?: string;
      currentUserCanEdit?: boolean;
    } = {
      NomeTipiTask: [...(this.filters.nome ?? [])],
      Priorita: [...(this.filters.priorita ?? [])],
      Stato: [...(this.filters.stato ?? [])],
    };

    const dataInizio = this.formatFilterDate(this.filters.dataInizio, false);
    const dataFine = this.formatFilterDate(this.filters.dataFine, true);

    if (dataInizio) searchObject.DataInizio = dataInizio;
    if (dataFine) searchObject.DataFine = dataFine;
    if (this.filters.statusType === TypeOfTaskStatus.PENDING) {
      searchObject.currentUserCanEdit = false;
    }

    const payload: any = { SearchObject: searchObject };

    if (this.filters.testo) {
      payload['SearchText'] = { Text: this.filters.testo };
    }

    payload['orderBy'] = this.currentSortColumns.map(s => ({
      propertyName: s.column,
      orderType: s.direction,
    }));

    const endpoint = this.filters.statusType === TypeOfTaskStatus.TO_COMPLETE
      ? 'api/taskapprovativi/my-tasks'
      : 'api/taskapprovativi/list';

    const params = {
      'Pager.PageSize': this.pageSize,
      'Pager.Page': pageIndex + 1,
    };

    const requestId = ++this.tasksLoadRequestId;
    this.tasksLoading = true;

    this.http.post<any>(endpoint, payload, { params }).pipe(
      finalize(() => {
        if (requestId === this.tasksLoadRequestId) {
          this.tasksLoading = false;
          this.refreshTasksObserver();
        }
      }),
    ).subscribe({
      next: (data) => {
        if (requestId !== this.tasksLoadRequestId) return;

        const loadedTasks = data.items.map((item: any) => ({
          ...item,
          descrizione: taskAnomalies(item.descrizione).join('; '),
          "primaryRole.name": item.primaryRole.name
        }));
        this.tasksArray = append
          ? this.appendUniqueTasks(loadedTasks)
          : loadedTasks;
        this.totalItems = data.totalItems;
        this.pageIndex = pageIndex;
        this.tasksInitialized = true;
      },
      error: () => {
        if (requestId !== this.tasksLoadRequestId) return;

        if (!append) {
          this.tasksArray = [];
          this.totalItems = 0;
        }
        this.tasksInitialized = true;
      }
    });
  }

  private loadNextTasksPage(): void {
    if (
      !this.isMobile ||
      !this.tasksInitialized ||
      this.tasksLoading ||
      this.tasksArray.length >= this.totalItems
    ) {
      return;
    }

    this.loadTasks(this.pageIndex + 1, true);
  }

  private appendUniqueTasks(loadedTasks: TaskApprovativo[]): TaskApprovativo[] {
    const taskIds = new Set(this.tasksArray.map(task => task.id));
    return [
      ...this.tasksArray,
      ...loadedTasks.filter(task => !taskIds.has(task.id)),
    ];
  }

  private refreshTasksObserver(): void {
    if (!this.tasksObserver || !this.loadMoreTrigger) return;

    requestAnimationFrame(() => {
      if (!this.tasksObserver || !this.loadMoreTrigger) return;
      this.tasksObserver.unobserve(this.loadMoreTrigger.nativeElement);
      this.tasksObserver.observe(this.loadMoreTrigger.nativeElement);
    });
  }

  private formatFilterDate(value: string | Date | null | undefined, endOfDay: boolean): string | undefined {
    if (!value) return undefined;

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;

    const pad = (part: number, length = 2) => part.toString().padStart(length, '0');
    const time = endOfDay ? '23:59:59.000' : '00:00:00.000';

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${time}`;
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTasks(this.pageIndex, false);
  }

  onSortChange(sortColumns: SortColumn[]) {
    this.currentSortColumns = sortColumns;
    this.pageIndex = 0;
    this.loadTasks(0, false);
  }

  onViewChange(value: string) {
    this.selectedView = value;
  }
  goToDetail(item: any) {
    let navigationArray = this.dynamicRouteService.getRoutePath(this.router, TASKS_DESCRIBE, 'task-detail');
    if(navigationArray) {
      navigationArray = this.dynamicRouteService.replacePathSegments(navigationArray, [item.id]);
      this.router.navigate(navigationArray, {});
    }
  }

  getTaskCountByStatus(status: number[]) {
    return this.http.post<number>('api/taskapprovativi/count', status);
  }
  getMyTaskCount() {
    return this.http.get<number>('api/taskapprovativi/count-my-tasks');
  }

  loadAllCountsByStatus() {
    forkJoin([
      this.getMyTaskCount(),
      this.getTaskCountByStatus([0,1]),
      this.getTaskCountByStatus([2]),
      this.getTaskCountByStatus([3]),
    ]).subscribe({
      next: (counts) => {
        this.newTaskCount = counts[0];
        this.pendingTaskCount = counts[1];
        this.completedTaskCount = counts[2];
        this.rejectedTaskCount = counts[3];
      }
    })
  }

  getChipCount(propName: string): number {
    switch (propName) {
      case 'newTaskCount': return this.newTaskCount;
      case 'pendingTaskCount': return this.pendingTaskCount;
      case 'completedTaskCount': return this.completedTaskCount;
      case 'rejectedTaskCount': return this.rejectedTaskCount;
      default: return 0;
    }
  }

  onChipSelectedChange(event: MatChipSelectionChange, statusType: TypeOfTaskStatus) {
    if (!event.selected) {
      // La pulsantiera deve avere sempre uno stato attivo.
      if (this.filters.statusType === statusType) {
        event.source.select();
      }
      return;
    }

    // select() emette un nuovo evento quando ripristiniamo la chip attiva.
    if (this.filters.statusType === statusType) return;

    // Mi salvo il tipo di stato selezionato
    this.filters.statusType = statusType;
    this.updateTitleLabel(statusType);

    const statusMap = {
      [TypeOfTaskStatus.TO_COMPLETE]: undefined,
      [TypeOfTaskStatus.PENDING]: [0, 1],
      [TypeOfTaskStatus.COMPLETED]: [2],
      [TypeOfTaskStatus.REJECTED]: [3]
    };
    this.filters.stato = statusMap[statusType];
    this.applyAndSave();
  }

  applyAndSave() {
    this.taskFilterService.setSavedFilters(this.filters); // Salva nel service
    this.pageIndex = 0;
    this.loadTasks(0, false);
    this.loadAllCountsByStatus();
  }

  updateTitleLabel(status: TypeOfTaskStatus) {
    const statusKeyMap = {
      [TypeOfTaskStatus.TO_COMPLETE]: 'filter.toComplete',
      [TypeOfTaskStatus.PENDING]: 'filter.pending',
      [TypeOfTaskStatus.COMPLETED]: 'filter.completed',
      [TypeOfTaskStatus.REJECTED]: 'filter.rejected'
    };
    this.titleLabel = this.translocoService.translate(`tasks.taskList.${statusKeyMap[status]}`).toLowerCase();
  }
}
